/**
 * Review / social page links for the Command Center header.
 *
 * Shared by every vertical and completely data-driven: it shows one small
 * button per platform the locations in scope ALREADY have a link saved for
 * (Guest Routing on the location), resolved server-side by
 * `GET /api/command-center/review-links`. Nothing is shown for a platform
 * nobody configured, and there is no data entry here.
 *
 * Brand marks come from the existing <PlatformIcon brand> set. A platform
 * with no brand mark (RateMDs, the custom link slots, anything whitelisted
 * later) is never an unlabeled mystery icon: it renders the neutral glyph
 * WITH a short text label.
 *
 * One location in scope → the button opens that page in a new tab.
 * Several → it opens a small list of the locations that have that link.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import PlatformIcon from '../../components/card_fleet/PlatformIcon';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

// Platforms the existing icon set has a real brand mark for. Everything
// else gets the neutral glyph plus a text label.
const BRANDED = new Set([
    'google_reviews', 'google', 'facebook', 'instagram',
    'tiktok', 'yelp', 'tripadvisor', 'trustpilot',
]);

const FALLBACK_LABEL = {
    ratemds: 'RateMDs',
};

const platformName = (key, t) => {
    const translated = t(`locations.routing.platform.${key}`, { defaultValue: '' });
    if (translated) return translated;
    if (FALLBACK_LABEL[key]) return FALLBACK_LABEL[key];
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const open = (url) => window.open(url, '_blank', 'noopener,noreferrer');

function PlatformButton({ platform, locations }) {
    const { t } = useTranslation();
    const [listOpen, setListOpen] = useState(false);
    const label = platformName(platform, t);
    const branded = BRANDED.has(platform);
    const single = locations.length === 1;

    const face = (
        <>
            <PlatformIcon platform={platform} size={20} brand />
            {!branded && <span className="max-w-[110px] truncate text-xs">{label}</span>}
        </>
    );
    // ~25% larger than the original h-9/16px mark, which also gives a
    // comfortable tap target on iPad. Links and behaviour unchanged.
    const classes = 'h-11 min-w-[44px] gap-1.5 rounded-full border-slate-200 bg-white px-3 text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200';

    if (single) {
        return (
            <Button variant="outline" size="sm" className={classes}
                title={`${label} · ${locations[0].name}`}
                aria-label={`${label} · ${locations[0].name}`}
                data-testid={`cc-review-link-${platform}`}
                onClick={() => open(locations[0].url)}>
                {face}
            </Button>
        );
    }

    return (
        <Popover open={listOpen} onOpenChange={setListOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={classes}
                    title={label} aria-label={label}
                    data-testid={`cc-review-link-${platform}`}>
                    {face}
                    <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{locations.length}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0" data-testid={`cc-review-link-list-${platform}`}>
                <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                    <PlatformIcon platform={platform} size={14} brand />
                    <span className="truncate text-sm font-medium">{label}</span>
                </div>
                <div className="max-h-64 overflow-y-auto py-1">
                    {locations.map((l) => (
                        <button key={l.location_id} type="button"
                            data-testid={`cc-review-link-${platform}-${l.location_id}`}
                            onClick={() => { setListOpen(false); open(l.url); }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50">
                            <span className="min-w-0 flex-1 truncate">{l.name}</span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        </button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default function ReviewLinksRow({ platforms, className }) {
    const { t } = useTranslation();
    if (!platforms || platforms.length === 0) return null;
    return (
        <div data-testid="cc-review-links"
            className={`flex flex-col gap-1.5 ${className || ''}`}>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
                data-testid="cc-review-links-title">
                {t('commandCenter.reviewLinks.title')}
            </span>
            <div className="flex flex-wrap items-center gap-2.5">
                {platforms.map((p) => (
                    <PlatformButton key={p.platform} platform={p.platform} locations={p.locations || []} />
                ))}
            </div>
        </div>
    );
}
